<section class="dry-run" aria-label="Connected process dry run">
  <header class="dry-run__header">
    <div>
      <h4>Connected process dry run</h4>
      <p>
        Simulated visits only. No live occurrences, assignments, evidence, waits
        or effects are created. Try page remains an isolated step preview.
      </p>
    </div>
    <div class="dry-run__buttons">
      <button
        uiButton
        type="button"
        (click)="check()"
        [disabled]="requestBlocked"
      >
        {{
          loading
            ? "Checking…"
            : result
              ? "Check scenario again"
              : "Start dry run"
        }}
      </button>
      <button
        uiButton
        type="button"
        (click)="restart()"
        [disabled]="requestBlocked"
      >
        Restart path
      </button>
    </div>
  </header>

  <div class="dry-run__source" aria-label="Dry run source">
    <strong
      >{{ draft.draftCode }} · saved revision {{ draft.revision }} · version
      {{ sourceDocument.definitionVersionCode }}</strong
    >
    <span>{{
      usesUnsaved
        ? "Includes applied unsaved settings"
        : "Saved settings only; unsaved changes are excluded"
    }}</span>
    <label *ngIf="unsavedSettings"
      ><input
        type="checkbox"
        [(ngModel)]="useUnsaved"
        (ngModelChange)="sourceChanged()"
        [disabled]="loading"
      />
      Include applied unsaved settings</label
    >
    <label
      ><input
        type="checkbox"
        [(ngModel)]="readOnly"
        (ngModelChange)="changeReadOnly()"
        [disabled]="loading"
      />
      Simulate read-only access</label
    >
    <details>
      <summary>Source identity</summary>
      <dl>
        <div>
          <dt>Saved content hash</dt>
          <dd>{{ draft.canonicalContentHash }}</dd>
        </div>
        <div>
          <dt>Saved ETag</dt>
          <dd>{{ draft.eTag }}</dd>
        </div>
        <ng-container *ngIf="result"
          ><div>
            <dt>Evaluated content hash</dt>
            <dd>{{ result.source.contentHash }}</dd>
          </div>
          <div>
            <dt>Payload / compiler</dt>
            <dd>
              {{ result.source.payloadSchemaVersion }} /
              {{ result.source.compilerVersion }}
            </dd>
          </div>
          <div>
            <dt>Configuration</dt>
            <dd>
              {{ result.source.configurationCode }} /
              {{ result.source.configurationVersionCode }}
            </dd>
          </div></ng-container
        >
      </dl>
    </details>
  </div>
  <p *ngIf="unappliedChanges" class="dry-run__notice" role="status">
    Unapplied editor fields are excluded. Apply them before including unsaved
    settings. Saved settings remain inspectable.
  </p>
  <p
    *ngIf="recoveryPending || sourceLoading"
    class="dry-run__notice"
    role="status"
  >
    Wait for the draft source or conflict recovery to finish before checking.
    Sample inputs remain available.
  </p>
  <p *ngIf="notice" class="dry-run__notice" role="status" aria-live="polite">
    {{ notice }}
  </p>
  <p *ngIf="error" class="dry-run__error" role="alert">{{ error }}</p>

  <section
    *ngIf="result && !loading"
    class="dry-run__result"
    aria-label="Connected dry run result"
    aria-live="polite"
  >
    <p *ngIf="scenarioEdited">
      Last checked result. Sample inputs have changed; simulate the action to
      check the updated scenario.
    </p>
    <p>
      Effective source:
      {{
        result.source.isUnsaved
          ? "includes unsaved settings"
          : "matches the saved revision after normalization"
      }}.
    </p>
    <strong>{{
      result.statusCode === "completed"
        ? "Simulated process completed"
        : result.statusCode === "read_only"
          ? "Read-only scenario · actions are unavailable"
          : result.statusCode === "blocked"
            ? "Simulation blocked"
            : "Ready for a simulated action"
    }}</strong>
    <p *ngIf="result.current">
      Current simulated visit: {{ result.current.label }} · visit
      {{ result.current.visitNumber }}.
    </p>
    <p *ngIf="result.current && !result.current.contractAvailable">
      The receiving step contract is incomplete. Inspect its page; connected
      completion is unavailable.
    </p>
    <ul *ngIf="result.diagnostics.length">
      <li *ngFor="let diagnostic of result.diagnostics">
        {{ diagnostic.message }}
      </li>
    </ul>
    <p>{{ result.automation.message }}</p>
    <p class="dry-run__boundary">
      Live execution rechecks current access, state and evidence. These results
      do not authorize execution.
    </p>
  </section>

  <div class="dry-run__inspect" *ngIf="steps.length">
    <label
      >Inspect step
      <select
        uiControl
        [(ngModel)]="selectedNodeId"
        (ngModelChange)="selectNode()"
      >
        <option *ngFor="let step of steps" [value]="step.id">
          {{ step.displayName || step.stepCode || step.id }}
        </option>
      </select></label
    >
    <div
      class="dry-run__buttons"
      role="group"
      aria-label="Simulated staff view"
    >
      <button
        uiButton
        type="button"
        [attr.aria-pressed]="view === 'detail'"
        (click)="view = 'detail'"
      >
        Staff page
      </button>
      <button
        uiButton
        type="button"
        [attr.aria-pressed]="view === 'dashboard'"
        (click)="view = 'dashboard'"
      >
        Staff worklist
      </button>
    </div>
    <label
      >Width
      <select uiControl [(ngModel)]="canvasSize">
        <option value="available">Available width</option>
        <option value="phone">Phone · 390px</option>
      </select></label
    >
  </div>
  <p *ngIf="node && !isCurrent" class="dry-run__notice">
    Inspection only. Viewing this destination creates no visit and carries no
    evidence. Start or resume the connected dry run to resolve the current step.
  </p>
  <p *ngIf="catalogLoading" role="status">Loading installed staff renderers…</p>
  <p *ngIf="catalogError" class="dry-run__error" role="alert">
    {{ catalogError }}
    <button uiButton type="button" (click)="loadCatalog()">
      Retry renderers
    </button>
  </p>
  <ng-container *ngIf="node as selectedNode">
    <label
      *ngIf="
        view === 'dashboard' && selectedNode.declaration?.dashboards?.length
      "
      >Dashboard
      <select uiControl [(ngModel)]="dashboardId">
        <option value="">Default authored dashboard</option>
        <option
          *ngFor="let dashboard of selectedNode.declaration?.dashboards"
          [value]="dashboard.id"
        >
          {{ dashboard.label }}
        </option>
      </select></label
    >
    <p *ngIf="view === 'dashboard'">
      Worklist rows are synthetic layout samples. They do not represent
      enrollment or assigned live work.
    </p>
    <phms-workflow-presentation-preview
      *ngIf="catalog"
      [document]="document"
      [node]="selectedNode"
      [scope]="view"
      [componentCatalog]="catalog"
      [dashboardId]="dashboardId"
      [canvasSize]="canvasSize"
      [sampleInputs]="visit.inputs"
      [sampleReadOnly]="previewReadOnly"
      [sampleVisitNumber]="currentVisit"
      [sampleVisitCompleted]="result?.statusCode === 'completed'"
      (sampleInputChange)="recordInput($event)"
    />
    <p *ngIf="!selectedNode.declaration">
      This step has no staff page contract. Its configuration remains
      inspectable; it cannot complete in a connected dry run.
    </p>
  </ng-container>

  <section
    class="dry-run__scenario"
    aria-label="Simulated action and gates"
    *ngIf="actions.length"
  >
    <div class="dry-run__scenario-actions">
      <label
        >Authored action
        <select
          uiControl
          [(ngModel)]="visit.action"
          (ngModelChange)="selectAction()"
          [disabled]="loading"
        >
          <option
            *ngFor="let candidate of actions"
            [value]="identity(candidate)"
          >
            {{ candidate.label }}
          </option>
        </select></label
      >
      <button
        uiButton
        type="button"
        severity="primary"
        [outlined]="false"
        (click)="simulateAction()"
        [disabled]="
          requestBlocked ||
          readOnly ||
          !isCurrent ||
          !result?.current?.contractAvailable ||
          !action
        "
      >
        Simulate action
      </button>
    </div>
    <ng-container *ngIf="action as selectedAction">
      <p *ngIf="!selectedAction.supported">
        This installed action has no supported simulation contract. Checking it
        reports the blocker without executing it.
      </p>
      <ul *ngIf="selectedAction.blockReasons.length">
        <li *ngFor="let reason of selectedAction.blockReasons">
          {{ reason.message }}
        </li>
      </ul>
      <p *ngIf="selectedAction.outcomes.length">
        Use the authored outcome and conditional reason on the staff form. For
        an action without a compatible form, choose its scenario outcome below.
      </p>
      <div
        *ngIf="selectedAction.outcomes.length && !usesOutcomeForm"
        class="dry-run__outcome"
      >
        <label
          >Scenario outcome
          <select
            uiControl
            [(ngModel)]="visit.outcomeCode"
            [disabled]="loading || readOnly"
          >
            <option value="">Choose outcome</option>
            <option
              *ngFor="let outcome of selectedAction.outcomes"
              [value]="outcome.code"
            >
              {{ outcome.label
              }}{{
                outcome.reasonPolicy === "required" ? " · reason required" : ""
              }}
            </option>
          </select></label
        >
        <label
          >Scenario reason
          <textarea
            uiControl
            [(ngModel)]="visit.reason"
            [disabled]="loading || readOnly"
            maxlength="4000"
          ></textarea>
        </label>
      </div>
      <p class="dry-run__destination">
        <strong>Before action:</strong> {{ beforeDestination }}
        <span
          *ngIf="selectedAction.completesStep && selectedAction.outcomes.length"
          >This is the authored destination; the server check resolves any
          connected routing and receiving contract.</span
        >
      </p>
    </ng-container>
    <details *ngIf="action?.requirements?.length" open>
      <summary>Requirement assumptions · no evidence is created</summary>
      <div class="dry-run__gates">
        <label *ngFor="let requirement of action?.requirements"
          >{{ requirement.id }}
          <select
            uiControl
            [ngModel]="requirementState(requirement.id)"
            (ngModelChange)="setRequirement(requirement.id, $event)"
            [disabled]="loading || readOnly"
          >
            <option
              *ngIf="!isAssumptionState(requirementState(requirement.id))"
              [value]="requirementState(requirement.id)"
              disabled
            >
              Server: {{ requirementState(requirement.id) }}
            </option>
            <option
              *ngFor="let state of requirementStates"
              [value]="state.code"
            >
              {{ state.label }}
            </option></select
          ><small>{{ requirementMessage(requirement) }}</small></label
        >
      </div>
    </details>
  </section>

  <details *ngIf="routingNodes.length" class="dry-run__routing">
    <summary>Conditional route assumptions</summary>
    <p>
      Supported conditions use the authored action result unless you supply an
      override. Initial route changes take effect when restarting the path.
    </p>
    <div *ngFor="let route of routingNodes" class="dry-run__route">
      <strong
        >{{ route.displayName || route.id }} ·
        {{ route.routing?.conditionCode }} v{{
          route.routing?.conditionVersion
        }}</strong
      >
      <label
        >Source state
        <select
          uiControl
          [(ngModel)]="routeInput(route).state"
          (ngModelChange)="routeChanged(route)"
          [disabled]="loading || readOnly"
        >
          <option *ngFor="let state of routeStates" [value]="state.code">
            {{ state.label }}
          </option>
        </select></label
      >
      <label
        >Sample determination
        <select
          uiControl
          [(ngModel)]="routeInput(route).outcomeCode"
          (ngModelChange)="routeChanged(route)"
          [disabled]="loading || readOnly"
        >
          <option value="">Choose outcome</option>
          <option
            *ngFor="let outcome of routeOutcomes(route)"
            [value]="outcome"
          >
            {{ outcome }}
          </option>
        </select></label
      >
    </div>
  </details>

  <details *ngIf="sourceDocument.processData?.fields?.length">
    <summary>Synthetic starting data</summary>
    <p>
      Supply sample values for the authored process fields. These values are
      never fetched from or written to live records.
    </p>
    <ul>
      <li *ngFor="let field of sourceDocument.processData?.fields">
        {{ field.label }}: {{ field.key }} ({{ field.type }}){{
          field.requiredAtStart ? " · required at start" : ""
        }}
      </li>
    </ul>
    <label
      >Sample values (JSON object)<textarea
        uiControl
        [(ngModel)]="startingDataJson"
        (ngModelChange)="startingDataChanged()"
        [disabled]="loading || readOnly"
      ></textarea>
    </label>
  </details>

  <section
    *ngIf="result?.trace?.length && !loading"
    class="dry-run__trace"
    aria-label="Simulated transitions and actions"
  >
    <h5>Simulated path · before and after</h5>
    <ol>
      <li *ngFor="let trace of result?.trace">
        <strong
          >{{ nodeLabel(trace.nodeId) }} · visit {{ trace.visitNumber }} ·
          {{
            trace.statusCode === "simulated" ? "Simulated" : "Blocked"
          }}</strong
        >
        <p>
          {{ trace.command.key.code
          }}{{ trace.outcomeCode ? " · " + trace.outcomeCode : "" }}
        </p>
        <p>
          <strong>Resolved destination:</strong>
          {{
            trace.destinationNodeId
              ? nodeLabel(trace.destinationNodeId)
              : "Current visit remains open"
          }}{{
            trace.destinationNodeId && !trace.receivingContractAvailable
              ? " · receiving contract unavailable"
              : ""
          }}
        </p>
        <ul *ngIf="trace.blockReasons.length">
          <li *ngFor="let reason of trace.blockReasons">
            {{ reason.message }}
          </li>
        </ul>
        <ul *ngIf="trace.requirements.length">
          <li *ngFor="let gate of trace.requirements">
            {{ gate.id }}: {{ gate.state }} — {{ gate.message }}
          </li>
        </ul>
        <p *ngIf="trace.selectedEdgeIds.length">
          Selected paths: {{ trace.selectedEdgeIds.join(", ") }}
        </p>
        <p *ngIf="trace.omittedEdgeIds.length">
          Omitted paths: {{ trace.omittedEdgeIds.join(", ") }}
        </p>
      </li>
    </ol>
  </section>
  <details *ngIf="hasInputs" class="dry-run__recovery">
    <summary>Recoverable sample inputs</summary>
    <p>
      Local sample inputs are kept separately for each simulated step visit.
      They are never live evidence.
    </p>
    <textarea
      uiControl
      readonly
      aria-label="Recoverable dry run inputs"
      [value]="recoveryCopy"
    ></textarea>
    <button uiButton type="button" (click)="clearInputs()" [disabled]="loading">
      Clear sample inputs and path
    </button>
  </details>
</section>
